import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SimpleTable } from "../../ui/SimpleTable/SimpleTable";
import { useFetchPagedContainerPlatform } from "../../../../../hooks/guide/useFetchPagedContainerPlatform";
import { ContainerPlatform } from "../../../../../types/api/guide/containerPlatform.types";
import { ROUTES } from "../../../../../constants/routes";
import { TypeContainerPlatforms } from "../../../../../types/api/guide/typeContainerPlatform.types";
import { ConfirmDialog } from "../../ConfirmDialog/ConfirmDialog";
import { containerPlatformService } from "../../../../../services/guide/containerPlatform-service";
import EditContainerPlatformsModal from "../../../../MapMonitoring/components/EditContainerPlatformsModal/EditContainerPlatformsModal";
import useAppSnackbar from "../../../../../hooks/useAppSnackbar";
import { useDebouncedValue } from "../../../../../hooks/useDebouncedValue";
import { PagedResultDto } from "../../../../../types/api/PagedResultDto";

interface ContainerSitesProps {
    openEdit?: (row: TypeContainerPlatforms) => void;
    canEdit?: boolean;
    title: string;
}

export const ContainerSitesTable = ({ title }: ContainerSitesProps) => {
    const navigate = useNavigate();
    const { showSuccess, showError, SnackbarComponent } = useAppSnackbar();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebouncedValue(search, 350);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    const { data: paged, isLoading, error, refetch } = useFetchPagedContainerPlatform(
        page,
        pageSize,
        debouncedSearch.trim() || undefined
    );

    const [toDelete, setToDelete] = useState<ContainerPlatform | null>(null);
    const [isOpenAdd, setOpenAdd] = useState<{ isOpen: boolean; id: string }>({
        isOpen: false,
        id: "",
    });

    const pageData = paged as PagedResultDto<ContainerPlatform> | null | undefined;
    const items = pageData?.items ?? [];
    const totalCount = pageData?.totalCount ?? 0;

    const handleDelete = async (id: string) => {
        try {
            await containerPlatformService.delete(id);
            showSuccess("Успешное удаление");
            refetch();
        } catch (e: any) {
            showError(e.response?.data);
            console.log("Ошибка при удалении допустимого типа загрузки");
        }
    };

    const handleAdd = () => {
        navigate(ROUTES.MAP.path, {
            state: { mapMode: "create-container-site" },
        });
    };

    return (
        <>
            <SimpleTable<ContainerPlatform>
                title={title}
                columns={[
                    { key: "name", title: "Название" },
                    { key: "typeCP", title: "Тип ПК" },
                    { key: "address", title: "Адрес" },
                    { key: "centerPoint", title: "Координаты" },
                    { key: "counterparty", title: "Контрагент" },
                    { key: "geofence", title: "Геозона" },
                ]}
                loading={isLoading}
                error={error}
                data={items}
                enableSearchByName
                searchPlaceholder="Поиск по названию"
                serverPagination={{
                    totalCount,
                    page,
                    pageSize,
                    onPageChange: setPage,
                    onPageSizeChange: (s) => {
                        setPageSize(s);
                        setPage(1);
                    },
                    searchValue: search,
                    onSearchChange: setSearch,
                }}
                getRowKey={(r) => r.id}
                getRowActions={(row) => ({
                    onEdit: () => setOpenAdd({ isOpen: true, id: row.id }),
                    onDelete: () => setToDelete(row),
                })}
                emptyText="Справочник пуст"
                setOpen={handleAdd}
            />

            <ConfirmDialog
                open={!!toDelete}
                title="Удалить контейнерную площадку?"
                description={toDelete ? `Будет удалена: ${toDelete.name}` : undefined}
                onCancel={() => setToDelete(null)}
                onConfirm={() => {
                    if (!toDelete) return;
                    handleDelete(toDelete.id);
                    setToDelete(null);
                }}
            />

            {isOpenAdd.isOpen && (
                <EditContainerPlatformsModal
                    id={isOpenAdd.id}
                    onClose={() => setOpenAdd({ isOpen: false, id: "" })}
                    refetchData={refetch}
                />
            )}
            <SnackbarComponent />
        </>
    );
};
